import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { 
  FiZap, 
  FiTarget, 
  FiTrendingUp, 
  FiShield, 
  FiDollarSign,
  FiCheckCircle,
  FiLoader,
  FiAlertTriangle,
  FiAlertCircle,
  FiInfo,
  FiCalendar,
  FiSliders,
  FiPlay
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import DepassementsParMois from '../DepassementsParMois';
import SliderResultDisplay from './SliderResultDisplay';

const SmartOptimizationPanel = ({ pdlData, results, session, onOptimize, analysisYear, savedStrategy }) => {
  // États pour l'optimisation automatique. Si une stratégie est déjà sauvée
  // en BDD (best_saving_strategy_json), on l'utilise comme état initial pour
  // restaurer l'affichage au retour sur la page sans re-lancer l'API.
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationResult, setOptimizationResult] = useState(
    savedStrategy ? { strategies: [savedStrategy] } : null
  );
  // Si savedStrategy arrive en async après le mount, on synchronise.
  useEffect(() => {
    if (savedStrategy && !optimizationResult) {
      setOptimizationResult({ strategies: [savedStrategy] });
    }
  }, [savedStrategy]); // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedStrategy, setSelectedStrategy] = useState('equilibre');
  const [expandedStrategy, setExpandedStrategy] = useState(null);
  const [error, setError] = useState(null);

  // États pour le mode slider
  const [showSliderMode, setShowSliderMode] = useState(false);
  const [winterPercentile, setWinterPercentile] = useState(99.5);
  const [summerPercentile, setSummerPercentile] = useState(99.2);
  const [sliderResult, setSliderResult] = useState(null);
  const [isSliderOptimizing, setIsSliderOptimizing] = useState(false);

  // Fonction utilitaire pour formater les devises
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Fonction pour basculer l'expansion des détails de stratégie
  const toggleStrategyExpansion = (index) => {
    setExpandedStrategy(expandedStrategy === index ? null : index);
  };

  // Configuration des stratégies - Version compacte pour 13"
  const strategies = {
    securite: {
      name: 'Sécurité Max',
      icon: <FiShield className="h-3 w-3" />,
      color: 'green',
      description: 'Approche conservatrice avec marge de sécurité élevée',
      details: 'P100 + 5% / P99.8%',
      apiValue: 'low'
    },
    equilibre: {
      name: 'Équilibre',
      icon: <FiTarget className="h-3 w-3" />,
      color: 'blue',
      description: 'Compromis idéal entre économies et sécurité',
      details: 'P99.5% / P99.2%',
      apiValue: 'balanced'
    },
    economie: {
      name: 'Économies Max',
      icon: <FiTrendingUp className="h-3 w-3" />,
      color: 'purple',
      description: 'Maximise les économies avec risque contrôlé',
      details: 'P98.5% / P99.2%',
      apiValue: 'high'
    }
  };

  // Fonction de gestion du changement de mode
  const handleModeSwitch = (isSliderMode) => {
    setShowSliderMode(isSliderMode);
    
    if (!isSliderMode) {
      setSliderResult(null);
      setIsSliderOptimizing(false);
      setWinterPercentile(99.5);
      setSummerPercentile(99.2);
    } else {
      setOptimizationResult(null);
      setError(null);
    }
  };

  // Fonction de simulation temps réel pour les sliders
  const handleSliderSimulation = useCallback(async (winter, summer) => {
  // On ajoute une sécurité pour s'assurer que toutes les props sont bien arrivées
  if (!pdlData || !session || !analysisYear) return;

  setIsSliderOptimizing(true);
  setError(null);

  try {
    const requestBody = {
      pdl_number: pdlData.pdl_number,
      user_id: pdlData.user_id || session?.user?.id,
      analysis_year: analysisYear, // Utilise la prop 'analysisYear'
      winter_percentile: winter / 100,
      summer_percentile: summer / 100
    };

    const response = await fetch('/api/v4/optimize-realtime-slider', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify(requestBody)
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.detail || 'Erreur de simulation');
    
    setSliderResult(result);

  } catch (error) {
    console.error('❌ Erreur de simulation slider:', error);
    setError(error.message);
    toast.error(error.message);
  } finally {
    setIsSliderOptimizing(false);
  }
}, [pdlData, session, analysisYear]); 

  // UseEffect pour la simulation slider
  useEffect(() => {
    if (showSliderMode) {
      const handler = setTimeout(() => {
        handleSliderSimulation(winterPercentile, summerPercentile);
      }, 500);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [winterPercentile, summerPercentile, showSliderMode, handleSliderSimulation]);

  // Fonction d'optimisation automatique
  const handleOptimization = async () => {
    setIsOptimizing(true);
    setError(null);
    const toastId = toast.loading('Vérification des données...');

    try {
        // --- VÉRIFICATIONS DÉTAILLÉES ---
        if (!pdlData) {
            throw new Error("Les informations du PDL (pdlData) sont manquantes.");
        }
        if (!pdlData.pdl_number) {
            throw new Error("Le numéro de PDL est manquant dans pdlData.");
        }
        // Pour les PDL ingérés via SFTP (sans pdl.user_id legacy), fallback sur
        // l'utilisateur connecté (= "qui déclenche l'analyse"). Cohérent avec
        // AcheminementDetail.jsx → moteur Python TURPE accepte n'importe quel
        // user_id non-null pour la traçabilité.
        const effectiveUserId = pdlData.user_id || session?.user?.id;
        if (!effectiveUserId) {
            throw new Error("L'ID utilisateur est manquant (ni pdlData.user_id ni session).");
        }
        if (!analysisYear) {
            throw new Error("L'année d'analyse est manquante.");
        }

        toast.loading('Lancement de l\'optimisation...', { id: toastId });

        const requestBody = {
            pdl_number: pdlData.pdl_number,
            user_id: effectiveUserId,
            risk_tolerance: strategies[selectedStrategy].apiValue,
            analysis_year: analysisYear
        };

        const response = await fetch('/api/v4/optimize-contract-smart', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify(requestBody)
        });

        const result = await response.json();

        if (!response.ok) {
            let errorMessage = result.detail || `Erreur serveur: ${response.status}`;
            if (Array.isArray(result.detail) && result.detail[0]?.msg) {
              errorMessage = `Validation échouée: ${result.detail[0].msg}`;
            }
            throw new Error(errorMessage);
        }

        setOptimizationResult(result);
        toast.success('Optimisation terminée — sélectionnez la stratégie retenue.', { id: toastId });
        // ⚠️ Pas d'appel à onOptimize ici : l'utilisateur doit choisir
        // explicitement parmi les 3 stratégies via le bouton "Choisir cette
        // stratégie" (sinon on imposait la recommandée IA par défaut).

    } catch (error) {
        console.error('❌ Erreur optimisation:', error);
        setError(error.message);
        toast.error(`Erreur: ${error.message}`, { id: toastId });
    } finally {
        setIsOptimizing(false);
    }
};

  // Composant pour les détails d'une stratégie - Version compacte
  const StrategyDetailPanel = ({ strategy, originalCost }) => {
    // PS souscrites réelles : moteur Python les stocke dans
    // composantes_detail.CS_FIXE.parametres (1 valeur par cadran). L'ancien
    // chemin `details_calcul.analytics.ps_*_kw` n'existait pas → fallback 60
    // activé pour tous les PDLs (bug visuel "60 kVA partout" sur SNB9 42).
    const csFixeParams = results?.turpe6_details?.composantes_detail?.CS_FIXE?.parametres || {};
    const originalPowers = results?.turpe6_details?.power_subscriptions || {
      hph: csFixeParams.ps_hph_kw ?? null,
      hch: csFixeParams.ps_hch_kw ?? null,
      hpb: csFixeParams.ps_hpb_kw ?? null,
      hcb: csFixeParams.ps_hcb_kw ?? null,
    };

    const optimizedPowers = strategy.new_power_subscriptions || {};
    const riskMetrics = strategy.risk_metrics || {};
    const breakdown = strategy.detailed_breakdown || {};
    
    const allExceedances = riskMetrics.all_exceedance_details || [];
    const billableExceedances = riskMetrics.billable_exceedances_monthly || [];

    return (
      <div className="mt-3 p-2 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {/* Puissances - Version compacte */}
          <div className="bg-white p-1.5 rounded border">
            <h6 className="font-medium text-gray-800 mb-2 flex items-center text-xs">
              <FiZap className="mr-1 h-3 w-3" />
              Puissances (kVA)
            </h6>
            <div className="space-y-1 text-xs">
              {Object.keys(optimizedPowers).map((period) => {
                const original = originalPowers[period] || 0;
                const optimized = optimizedPowers[period] || 0;
                const variation = original ? ((optimized - original) / original) * 100 : 0;
                return (
                  <div key={period} className="flex justify-between items-center">
                    <span className="font-medium">{period.toUpperCase()}</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600">{parseFloat(original).toFixed(0)}</span>
                      <span>→</span>
                      <span className="font-bold">{parseFloat(optimized).toFixed(0)}</span>
                      <span className={`font-medium ${variation >= 0 ? 'text-red-500' : 'text-green-600'}`}>
                        ({variation.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Calcul du gain - Version compacte */}
          <div className="bg-white p-1.5 rounded border">
            <h6 className="font-medium text-gray-800 mb-2 flex items-center text-xs">
              <FiDollarSign className="mr-1 h-3 w-3" />
              Gain Net Annuel
            </h6>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600">Économie abonnement :</span>
                <span className="font-medium text-green-600">
                  + {formatCurrency(breakdown.savings_by_component?.fixe || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Coût risque (CMDPS) :</span>
                <span className="font-medium text-red-600">
                  - {formatCurrency(riskMetrics.expected_annual_overrun_eur || 0)}
                </span>
              </div>
              <hr className="my-1"/>
              <div className="flex justify-between font-bold">
                <span className="text-gray-800">= Gain Net :</span>
                <span className="text-blue-700">
                  {formatCurrency(strategy.annual_saving_eur)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Analyse de Risque - Version compacte */}
        <div className="bg-white p-1.5 rounded border">
          <h6 className="font-medium text-orange-800 mb-2 flex items-center text-xs">
            <FiAlertCircle className="mr-1 h-3 w-3" />
            Analyse de Risque
          </h6>
          
          <div className="grid grid-cols-2 gap-2 mb-1.5">
            <div className="bg-orange-50 p-2 rounded text-center">
              <div className="text-xs text-orange-700">Nb. dépassements</div>
              <div className="text-xs font-bold text-orange-600">{allExceedances.length}</div>
            </div>
            <div className="bg-red-50 p-2 rounded text-center">
              <div className="text-xs text-red-700">Coût total/an</div>
              <div className="text-xs font-bold text-red-600">
                {formatCurrency(riskMetrics.expected_annual_overrun_eur || 0)}
              </div>
            </div>
          </div>

          {allExceedances.length > 0 ? (
            <div>
              <h5 className="text-xs font-medium text-gray-700 mb-2">Détail par mois :</h5>
              <DepassementsParMois 
                billableExceedances={billableExceedances}
                allExceedances={allExceedances} 
              />
            </div>
          ) : (
            <div className="text-center p-2 bg-green-50 rounded text-green-700 text-xs font-medium">
              Aucun dépassement prévu
            </div>
          )}
        </div>
      </div>
    );
  };

  // Composant tableau responsive optimisé pour 13"
  const OptimizationStrategiesTable = ({ optimizationResult, expandedStrategy, toggleStrategyExpansion }) => {
    const viableStrategies = useMemo(() => {
      if (!optimizationResult?.strategies) return [];
      return optimizationResult.strategies.filter(strategy => strategy.annual_saving_eur > 0);
    }, [optimizationResult]);

    if (viableStrategies.length === 0) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-center">
          <FiInfo className="h-8 w-8 text-blue-500 mx-auto mb-2" />
          <h4 className="font-bold text-blue-800 mb-1 text-xs">
            Aucune stratégie viable trouvée
          </h4>
          <p className="text-blue-700 text-xs">
            Votre contrat semble déjà optimal pour cette période.
          </p>
        </div>
      );
    }
    
    return (
      <div className="space-y-1.5">
        <h4 className="font-medium text-gray-800 flex items-center text-xs">
          <FiTarget className="mr-2 h-3 w-3 text-blue-600" />
          Stratégies viables ({viableStrategies.length})
        </h4>
        
        {/* Version unique optimisée pour 13" */}
        <div className="space-y-1.5">
          {viableStrategies.map((strategy, index) => {
            const isChosen = strategy.strategy_name === optimizationResult?._chosenStrategyName;
            return (
            <div key={index} className={`bg-white rounded-lg border shadow-sm ${
              isChosen ? 'border-emerald-500 ring-2 ring-emerald-200' :
              strategy.is_recommended ? 'border-yellow-300' : 'border-gray-200'
            }`}>
              <div
                className={`p-1.5 cursor-pointer transition-all ${
                  isChosen ? 'bg-emerald-50 border-l-4 border-emerald-500' :
                  strategy.is_recommended ? 'bg-yellow-50 border-l-4 border-yellow-400' : 'hover:bg-gray-50'
                }`}
                onClick={() => toggleStrategyExpansion(index)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h5 className="font-medium text-gray-900 text-xs">{strategy.strategy_name}</h5>
                    {strategy.is_recommended && (
                      <span className="bg-yellow-400 text-white px-2 py-0.5 rounded-full text-[10px] font-medium">
                        ⭐ Recommandée IA
                      </span>
                    )}
                    {isChosen && (
                      <span className="bg-emerald-600 text-white px-2 py-0.5 rounded-full text-[10px] font-medium">
                        ✓ Choisie
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {expandedStrategy === index ? '▼' : '▶'}
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div className="bg-green-50 p-2 rounded text-center">
                    <div className="text-green-700">Gain/an</div>
                    <div className="font-bold text-green-600">{formatCurrency(strategy.annual_saving_eur)}</div>
                    {(strategy.gain_ps_seule_eur != null || strategy.gain_bascule_version_eur != null) && (
                      <div className="text-[10px] text-gray-500 mt-0.5 leading-tight">
                        {strategy.gain_ps_seule_eur != null && (
                          <span title="Économie sur la composante de soutirage à version d'utilisation constante">
                            PS : <span className="font-medium text-gray-700">{formatCurrency(strategy.gain_ps_seule_eur)}</span>
                          </span>
                        )}
                        {strategy.gain_ps_seule_eur != null && strategy.gain_bascule_version_eur != null && Number(strategy.gain_bascule_version_eur) !== 0 && (
                          <span className="mx-1 text-gray-300">·</span>
                        )}
                        {strategy.gain_bascule_version_eur != null && Number(strategy.gain_bascule_version_eur) !== 0 && (
                          <span title="Économie supplémentaire si on bascule de version d'utilisation (CU ↔ LU)">
                            Version : <span className="font-medium text-gray-700">{formatCurrency(strategy.gain_bascule_version_eur)}</span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="bg-red-50 p-2 rounded text-center">
                    <div className="text-red-700">Risque</div>
                    <div className="font-bold text-red-600">{formatCurrency(strategy.risk_metrics?.expected_annual_overrun_eur || 0)}</div>
                  </div>
                  <div className="bg-purple-50 p-2 rounded text-center">
                    <div className="text-purple-700">ROI</div>
                    <div className="font-bold text-purple-600">{strategy.payback_months}m</div>
                  </div>
                  <div className="bg-orange-50 p-2 rounded text-center">
                    <div className="text-orange-700">Dépass.</div>
                    <div className="font-bold text-orange-600">
                      {strategy.risk_metrics?.billable_exceedances_monthly?.length || 0}
                    </div>
                  </div>
                </div>
              </div>
              
              {expandedStrategy === index && (
                <div className="border-t">
                  <StrategyDetailPanel
                    strategy={strategy}
                    originalCost={optimizationResult.original_annual_cost_turpe6_ttc}
                  />
                </div>
              )}

              {/* Bouton de validation de la stratégie : remonte le choix au parent */}
              <div className="border-t px-1.5 py-1.5 flex justify-end">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Force is_recommended sur la stratégie choisie pour que
                    // l'éco totale (computeTotalSavings) prenne celle-ci.
                    const chosenResult = {
                      ...optimizationResult,
                      _chosenStrategyName: strategy.strategy_name,
                      strategies: optimizationResult.strategies.map(s => ({
                        ...s,
                        is_recommended: s.strategy_name === strategy.strategy_name,
                      })),
                    };
                    setOptimizationResult(chosenResult);
                    if (onOptimize) onOptimize(chosenResult);
                    toast.success(`Stratégie "${strategy.strategy_name}" enregistrée`);
                  }}
                  className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                    isChosen
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-300 cursor-default'
                      : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
                  disabled={isChosen}
                >
                  {isChosen ? '✓ Stratégie retenue' : 'Choisir cette stratégie'}
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2 p-2">
      {/* Header compact avec onglets */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-2 rounded-lg border border-blue-200">
        <h3 className="text-xs font-bold text-gray-800 mb-1.5 flex items-center">
          <FiZap className="w-5 h-5 text-blue-600 mr-2" />
          Optimisation IA
        </h3>
        
        {/* Onglets compacts */}
        <div className="flex space-x-2 mb-1.5">
          <button
            onClick={() => handleModeSwitch(false)}
            className={`px-1.5 py-2 rounded text-xs font-medium transition-colors ${
              !showSliderMode
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <FiTarget className="mr-1 h-3 w-3 inline" />
            Automatique
          </button>
          <button
            onClick={() => handleModeSwitch(true)}
            className={`px-1.5 py-2 rounded text-xs font-medium transition-colors ${
              showSliderMode
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <FiSliders className="mr-1 h-3 w-3 inline" />
            Sliders
          </button>
        </div>

        {/* Description compacte */}
        <p className="text-gray-600 text-xs">
          {!showSliderMode 
            ? "L'IA calcule automatiquement la meilleure stratégie selon votre profil de risque."
            : "Ajustez manuellement les percentiles pour voir l'impact en temps réel."
          }
        </p>
      </div>

      {/* Contenu conditionnel selon le mode */}
      {!showSliderMode ? (
        <>
          {/* Mode Automatique - Design compact */}
          <div className="bg-white p-2 rounded-lg border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-1.5 flex items-center text-xs">
              <FiTarget className="mr-2 h-3 w-3" />
              Profil d'optimisation
            </h4>
            
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(strategies).map(([key, strategy]) => (
                <div
                  key={key}
                  onClick={() => setSelectedStrategy(key)}
                  className={`p-1.5 rounded border-2 cursor-pointer transition-all text-center ${
                    selectedStrategy === key
                      ? `border-${strategy.color}-500 bg-${strategy.color}-50` 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white mb-2 ${
                    strategy.color === 'green' ? 'bg-green-500' :
                    strategy.color === 'blue' ? 'bg-blue-500' : 'bg-purple-500'
                  }`}>
                    {strategy.icon}
                  </div>
                  <h5 className="font-medium text-gray-900 text-xs mb-1">{strategy.name}</h5>
                  <p className="text-xs text-gray-500">{strategy.details}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Bouton d'optimisation compact */}
          <div className="text-center">
            <button
              onClick={handleOptimization}
              disabled={isOptimizing}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-2 py-1.5 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto text-xs"
            >
              {isOptimizing ? (
                <>
                  <FiLoader className="animate-spin mr-2 h-3 w-3" />
                  Optimisation...
                </>
              ) : (
                <>
                  <FiPlay className="mr-2 h-3 w-3" />
                  Lancer l'optimisation
                </>
              )}
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Mode Sliders - Design compact */}
          <div className="bg-white p-2 rounded-lg border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-1.5 flex items-center text-xs">
              <FiSliders className="mr-2 h-3 w-3" />
              Ajustement Percentiles
            </h4>
            
            <div className="space-y-2">
              {/* Slider Hiver */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Hiver (HPH/HCH): {winterPercentile.toFixed(1)}%
                </label>
                <input
                  type="range"
                  min="90"
                  max="100"
                  step="0.1"
                  value={winterPercentile}
                  onChange={(e) => setWinterPercentile(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>90%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* Slider Été */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Été (HPB/HCB): {summerPercentile.toFixed(1)}%
                </label>
                <input
                  type="range"
                  min="90"
                  max="100"
                  step="0.1"
                  value={summerPercentile}
                  onChange={(e) => setSummerPercentile(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>90%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>

            <div className="mt-3 p-1.5 bg-amber-50 rounded border border-amber-200">
              <p className="text-xs text-amber-800">
                Plus le percentile est élevé, plus vous êtes protégé, mais moins vous économisez.
              </p>
            </div>
          </div>

          {/* Affichage du résultat slider */}
          <SliderResultDisplay 
            result={sliderResult}
            isOptimizing={isSliderOptimizing}
            originalCost={results?.turpe6_details?.cout_total_annuel || 0}
          />
        </>
      )}

      {/* Affichage des erreurs */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-1.5">
          <div className="flex items-center">
            <FiAlertTriangle className="h-3 w-3 text-red-500 mr-2" />
            <p className="text-red-700 font-medium text-xs">Erreur</p>
          </div>
          <p className="text-red-600 text-xs mt-1">{error}</p>
        </div>
      )}

      {/* Résultats d'optimisation automatique */}
      {!showSliderMode && optimizationResult && (
        (() => {
          const viableStrategies = optimizationResult.strategies?.filter(s => s.annual_saving_eur > 0) || [];
          
          return viableStrategies.length === 0 ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-center">
              <FiInfo className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <h4 className="font-bold text-blue-800 mb-1 text-xs">
                Aucune stratégie viable
              </h4>
              <p className="text-blue-700 text-xs">
                Votre contrat semble déjà optimisé.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Résumé global compact */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-2 rounded-lg border border-green-200">
                <h4 className="font-bold text-gray-800 mb-1.5 flex items-center text-xs">
                  <FiCheckCircle className="mr-2 h-3 w-3 text-green-600" />
                  Optimisation terminée - {viableStrategies.length} stratégie{viableStrategies.length > 1 ? 's' : ''}
                </h4>
                
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center p-2 bg-white rounded shadow-sm">
                    <div className="text-gray-600">Coût actuel/an</div>
                    <div className="font-bold text-red-600">
                      {formatCurrency(optimizationResult.original_annual_cost_turpe6_ttc)}
                    </div>
                  </div>
                  <div className="text-center p-2 bg-white rounded shadow-sm">
                    <div className="text-gray-600">Meilleure économie</div>
                    <div className="font-bold text-green-600">
                      {formatCurrency(Math.max(...viableStrategies.map(s => s.annual_saving_eur)))}
                    </div>
                  </div>
                  <div className="text-center p-2 bg-white rounded shadow-sm">
                    <div className="text-gray-600">ROI le plus rapide</div>
                    <div className="font-bold text-purple-600">
                      {Math.min(...viableStrategies.map(s => s.payback_months))} mois
                    </div>
                  </div>
                </div>
              </div>

              {/* Tableau responsive compact */}
              <OptimizationStrategiesTable 
                optimizationResult={{...optimizationResult, strategies: viableStrategies}}
                expandedStrategy={expandedStrategy}
                toggleStrategyExpansion={toggleStrategyExpansion}
              />
            </div>
          );
        })()
      )}
    </div>
  );
};

export default SmartOptimizationPanel;